import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import clinicAPI from "../../../services/clinicApi";
import { formatDateRu, formatTimeRu } from "../../../utils/appointmentLabels";
import {
  defaultClosesAtFromDatetime,
  defaultClosesAtFromSession,
  pollTypeLabel,
} from "../../../utils/pollDisplay";
import {
  defaultScheduledSendAt,
  HOURS_AFTER_OPTIONS,
  HOURS_BEFORE_OPTIONS,
  inferTriggerType,
  isScheduledInFuture,
  POLL_TRIGGER_OPTIONS,
  pollTriggerLabel,
  toDatetimeLocalValue,
} from "../../../utils/pollSchedule";
import PollDateTimeField from "./PollDateTimeField";
import s from "../clinic.module.css";
import { useAdminHubUi } from "../../../hooks/useAdminAppMode";
import aa from "../AdminPortal/adminApp.module.css";
import md from "../common/manageDesktop.module.css";
import { HubFormCard, HubListGroup, HubNavAdd, HubRow, HubScreen, HubSegment, HubSheet, HubSheetActions, DesktopDialog } from "../AdminPortal/HubPwa";
import MailingsPanel from "./MailingsPanel";
import SwipeableRow from "../AdminPortal/SwipeableRow";
import {
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  Segment,
  Select,
  useToast,
} from "../../ui";
import { ActTarget, ActivationPageGuide } from "../../../activation";

/** Push-рассылки без опроса — временно скрыты в интерфейсе. */
const POLL_MAILINGS_ENABLED = false;

const DEFAULT_POLL_OPTIONS = [
  { id: "o1", text: "", forecast: null },
  { id: "o2", text: "", forecast: null },
];

function emptyDraft() {
  return {
    title: "",
    description: "",
    poll_type: "custom",
    question: "",
    options: DEFAULT_POLL_OPTIONS.map((o) => ({ ...o })),
    group_ids: [],
    scheduled_send_at: defaultScheduledSendAt(),
    closes_at: "",
    trigger_type: "immediate",
    hours_before: 2,
    hours_after: 1,
  };
}

function PollEditor({
  draft,
  onChange,
  groups,
  sessions,
  onClose,
  onSave,
  saving,
  isEdit,
  isApp = false,
}) {
  const set = (patch) => onChange({ ...draft, ...patch });

  const toggleGroup = (id) => {
    const gid = String(id);
    const has = draft.group_ids.includes(gid);
    set({
      group_ids: has
        ? draft.group_ids.filter((x) => x !== gid)
        : [...draft.group_ids, gid],
    });
  };

  const updateOption = (idx, patch) => {
    const options = draft.options.map((o, i) =>
      i === idx ? { ...o, ...patch } : o,
    );
    set({ options });
  };

  const addOption = () => {
    const n = draft.options.length + 1;
    set({
      options: [
        ...draft.options,
        { id: `o${n}`, text: "", forecast: null },
      ],
    });
  };

  const setTriggerType = (trigger_type) => {
    if (trigger_type === "before_session" || trigger_type === "after_session") {
      set({
        trigger_type,
        closes_at: "",
      });
      return;
    }
    if (trigger_type === "scheduled") {
      set({
        trigger_type,
        scheduled_send_at: draft.scheduled_send_at || defaultScheduledSendAt(),
        closes_at: draft.closes_at || "",
      });
      return;
    }
    if (trigger_type === "immediate") {
      set({
        trigger_type,
        closes_at: "",
      });
      return;
    }
    set({ trigger_type });
  };

  const showScheduledSend = draft.trigger_type === "scheduled";

  const title = isEdit ? "Редактировать опрос" : "Создать опрос";
  const primaryLabel = saving
    ? "Сохранение…"
    : isEdit
      ? "Сохранить"
      : draft.trigger_type === "before_session" ||
          draft.trigger_type === "after_session"
        ? "Создать правило"
        : draft.trigger_type === "scheduled" &&
            isScheduledInFuture(draft.scheduled_send_at)
          ? "Запланировать"
          : "Создать и отправить";

  const formClass = isApp ? aa.pollEditorSheetForm : aa.pollEditorForm;
  const hintsClass = aa.pollEditorSheetHints;

  const editorFooter = (
    <HubSheetActions
      primary={primaryLabel}
      onSecondary={onClose}
      onPrimary={onSave}
      primaryDisabled={saving}
    />
  );

  const fields = (
    <div className={formClass}>
      <HubFormCard title="Основное">
        <Field label="Название">
          <Input
            value={draft.title}
            onChange={(e) => set({ title: e.target.value })}
            placeholder="Опрос перед соревнованием"
            autoFocus
          />
        </Field>
        <Field label="Вопрос">
          <Input
            value={draft.question}
            onChange={(e) => set({ question: e.target.value })}
            placeholder="Какой вариант вам подходит?"
          />
        </Field>
      </HubFormCard>

      <HubFormCard title="Варианты ответа">
        <ul className={aa.pollOptionList}>
          {draft.options.map((opt, idx) => (
            <li key={opt.id || idx} className={aa.pollOptionRow}>
              <Input
                value={opt.text}
                onChange={(e) => updateOption(idx, { text: e.target.value })}
                placeholder={`Вариант ${idx + 1}`}
              />
            </li>
          ))}
        </ul>
        <Button type="button" size="sm" variant="secondary" onClick={addOption}>
          + Вариант
        </Button>
      </HubFormCard>

      <HubFormCard title="Когда отправить">
        <div className={aa.pollSendModeCards}>
          {POLL_TRIGGER_OPTIONS.map((opt) => (
            <button
              key={opt.id}
              type="button"
              className={
                draft.trigger_type === opt.id
                  ? aa.pollSendModeCardOn
                  : aa.pollSendModeCard
              }
              onClick={() => setTriggerType(opt.id)}
            >
              <strong>{opt.label}</strong>
              <small>{opt.hint}</small>
            </button>
          ))}
        </div>

        {showScheduledSend ? (
          <Field label="Дата и время отправки">
            <PollDateTimeField
              value={draft.scheduled_send_at}
              onChange={(scheduled_send_at) => set({ scheduled_send_at })}
              hint="Клиенты получат push в указанное время"
            />
          </Field>
        ) : null}

        {draft.trigger_type === "before_session" ? (
          <Field label="За сколько часов до тренировки">
            <Select
              value={String(draft.hours_before || 2)}
              onChange={(e) => set({ hours_before: Number(e.target.value) })}
            >
              {HOURS_BEFORE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}

        {draft.trigger_type === "after_session" ? (
          <Field label="Через сколько часов после тренировки">
            <Select
              value={String(draft.hours_after || 1)}
              onChange={(e) => set({ hours_after: Number(e.target.value) })}
            >
              {HOURS_AFTER_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}
      </HubFormCard>

      <HubFormCard title="Группы">
        <div className={aa.pollGroupGrid}>
          {groups.length === 0 ? (
            <p className={aa.hubHint}>
              Сначала создайте группу в разделе «Группы».
            </p>
          ) : (
            groups.map((g) => {
              const on = draft.group_ids.includes(String(g.id));
              return (
                <button
                  key={g.id}
                  type="button"
                  className={on ? aa.pollGroupChipOn : aa.pollGroupChip}
                  aria-pressed={on}
                  onClick={() => toggleGroup(g.id)}
                >
                  {g.name}
                </button>
              );
            })
          )}
        </div>
      </HubFormCard>

      <aside className={hintsClass}>
        {draft.trigger_type === "before_session" ? (
          <p>
            Перед каждым занятием выбранных групп клиенты получат push. Ответ
            не привязан к занятию в календаре.
          </p>
        ) : draft.trigger_type === "after_session" ? (
          <p>
            После каждого занятия клиенты получат опрос через указанное время.
          </p>
        ) : draft.trigger_type === "scheduled" ? (
          <p>Опрос будет отправлен автоматически в выбранное время.</p>
        ) : (
          <p>
            Опрос уйдёт клиентам сразу после создания. Срок ответа — 7 дней,
            если не указан отдельно.
          </p>
        )}
      </aside>
    </div>
  );

  if (isApp) {
    return (
      <HubSheet
        open
        onClose={onClose}
        title={title}
        titleId="poll-editor-title"
        bodyClassName={aa.pollEditorSheetBody}
        footer={editorFooter}
      >
        {fields}
      </HubSheet>
    );
  }

  return (
    <DesktopDialog
      open
      onClose={onClose}
      title={title}
      titleId="poll-editor-title"
      wide
      footer={editorFooter}
      bodyClassName={aa.pollEditorDialogBody}
    >
      {fields}
    </DesktopDialog>
  );
}

function PollResults({ pollId, onClose, isApp = false }) {
  const toast = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    clinicAPI
      .getTrainingPollResults(pollId)
      .then(setData)
      .catch((e) => {
        toast.error(e.response?.data?.error || "Не удалось загрузить");
        onClose();
      })
      .finally(() => setLoading(false));
  }, [pollId, onClose, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const extend = async () => {
    try {
      await clinicAPI.extendTrainingPoll(pollId, { hours: 2 });
      toast.success("Дедлайн продлён на 2 часа");
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    }
  };

  if (loading) {
    if (isApp) {
      return (
        <HubSheet open onClose={onClose} title="Итоги опроса">
          <p className={aa.hubHint}>Загрузка…</p>
        </HubSheet>
      );
    }
    return (
      <div className={s.pollsEditorBackdrop} onClick={onClose} role="presentation">
        <aside className={s.pollsEditor} onClick={(e) => e.stopPropagation()}>
          <p className={s.pollsEditorIntro}>Загрузка…</p>
        </aside>
      </div>
    );
  }

  const poll = data?.poll;
  const stats = data?.stats || {};
  const roster = data?.roster || [];
  const isAttendance = poll?.poll_type === "attendance";

  const resultsBody = (
    <>
      <div className={isApp ? aa.pollResultsBody : s.pollsEditorBody}>
        <div className={s.pollsStatsRow}>
          <span>
            Отправлено: <strong>{stats.sent ?? 0}</strong>
          </span>
          <span>
            Ответили: <strong>{stats.responded ?? 0}</strong>
          </span>
          <span>
            Ждут: <strong>{stats.pending ?? 0}</strong>
          </span>
          {isAttendance && stats.forecast_coming != null ? (
            <span className={s.pollsForecastBadge}>
              Прогноз «приду»: <strong>{stats.forecast_coming}</strong>
            </span>
          ) : null}
        </div>

        {(stats.by_option || []).length > 0 ? (
          <ul className={s.pollsOptionStats}>
            {stats.by_option.map((o) => (
              <li key={o.id}>
                {o.text}: <strong>{o.count}</strong>
              </li>
            ))}
          </ul>
        ) : null}

        {roster.length === 0 ? (
          <EmptyState title="Пока нет ответов" description="Клиенты увидят опрос в личном кабинете" />
        ) : (
          <ul className={s.pollsRosterList}>
            {roster.map((r) => (
              <li
                key={r.dispatch_id}
                className={`${s.pollsRosterRow} ${s[`pollsRosterRow_${r.status}`]}`}
              >
                <span className={s.pollsRosterName}>{r.full_name}</span>
                <span className={s.pollsRosterStatus}>{r.status_label}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {!isApp ? (
        <footer className={s.pollsEditorFoot}>
          {poll?.status === "active" ? (
            <Button type="button" variant="secondary" onClick={extend}>
              Продлить на 2 ч
            </Button>
          ) : null}
          <Button type="button" onClick={onClose}>
            Закрыть
          </Button>
        </footer>
      ) : (
        <div className={aa.pollResultsFoot}>
          {poll?.status === "active" ? (
            <button type="button" className={aa.hubSecondaryCta} onClick={extend}>
              Продлить на 2 ч
            </button>
          ) : null}
        </div>
      )}
    </>
  );

  if (isApp) {
    return (
      <HubSheet
        open
        onClose={onClose}
        title={poll?.title || "Итоги"}
        subtitle={pollTypeLabel(poll?.poll_type)}
      >
        {resultsBody}
      </HubSheet>
    );
  }

  return (
    <div className={s.pollsEditorBackdrop} onClick={onClose} role="presentation">
      <aside
        className={`${s.pollsEditor} ${s.pollsEditor_wide}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
      >
        <header className={s.pollsEditorHead}>
          <div>
            <h3>{poll?.title}</h3>
            <p className={s.pollsCardMeta}>{pollTypeLabel(poll?.poll_type)}</p>
          </div>
          <button
            type="button"
            className={s.pollsEditorClose}
            onClick={onClose}
            aria-label="Закрыть"
          >
            ×
          </button>
        </header>
        {resultsBody}
      </aside>
    </div>
  );
}

export default function AdminPolls() {
  const toast = useToast();
  const isApp = useAdminHubUi();
  const [polls, setPolls] = useState([]);
  const [groups, setGroups] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(emptyDraft);
  const [saving, setSaving] = useState(false);
  const [resultsId, setResultsId] = useState(null);
  const [section, setSection] = useState("polls");
  const [mailingEditorOpen, setMailingEditorOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await clinicAPI.getTrainingPolls();
      setPolls(data.polls || []);
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка загрузки");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
    clinicAPI
      .getSportGroupsSummary()
      .then((r) => setGroups(r.groups || []))
      .catch(() => {});
  }, [load]);

  useEffect(() => {
    if (!draft.group_ids.length) {
      setSessions([]);
      return;
    }
    const from = new Date().toISOString().slice(0, 10);
    const toDate = new Date();
    toDate.setDate(toDate.getDate() + 45);
    const to = toDate.toISOString().slice(0, 10);
    clinicAPI
      .getTrainingSessions({ from, to })
      .then((r) => {
        const gset = new Set(draft.group_ids.map(String));
        setSessions(
          (r.sessions || []).filter((sess) => {
            const gid = sess.group?.id || sess.specialization_id;
            return gid && gset.has(String(gid));
          }),
        );
      })
      .catch(() => setSessions([]));
  }, [draft.group_ids]);

  const activePolls = useMemo(
    () => polls.filter((p) => p.status === "active"),
    [polls],
  );

  const openCreate = () => {
    setEditingId(null);
    setDraft(emptyDraft());
    setEditorOpen(true);
  };

  const openEdit = (poll) => {
    setEditingId(poll.id);
    const trigger_type = inferTriggerType(poll);
    setDraft({
      title: poll.title || "",
      description: poll.description || "",
      poll_type: poll.poll_type || "custom",
      question: poll.questions?.[0]?.text || "",
      options:
        poll.options?.length > 0
          ? poll.options.map((o, i) => ({
              id: o.id || `o${i + 1}`,
              text: o.text || "",
              forecast: o.forecast || null,
            }))
          : DEFAULT_POLL_OPTIONS.map((o) => ({ ...o })),
      group_ids: (poll.group_ids || []).map(String),
      scheduled_send_at: poll.scheduled_send_at
        ? toDatetimeLocalValue(poll.scheduled_send_at)
        : defaultScheduledSendAt(),
      closes_at: poll.closes_at
        ? toDatetimeLocalValue(poll.closes_at)
        : "",
      trigger_type,
      hours_before: poll.hours_before || 2,
      hours_after: poll.hours_after || 1,
    });
    setEditorOpen(true);
  };

  const savePoll = async () => {
    if (!draft.title.trim()) {
      toast.error("Укажите название");
      return;
    }
    if (!draft.group_ids.length) {
      toast.error("Выберите группы");
      return;
    }
    if (draft.trigger_type === "scheduled" && !draft.scheduled_send_at) {
      toast.error("Укажите дату и время отправки");
      return;
    }
    if (!draft.question.trim()) {
      toast.error("Укажите вопрос");
      return;
    }
    const options = draft.options.filter((o) => o.text.trim());
    if (!options.length) {
      toast.error("Добавьте варианты ответа");
      return;
    }
    setSaving(true);
    try {
      const recurring =
        draft.trigger_type === "before_session" ||
        draft.trigger_type === "after_session";
      const payload = {
        title: draft.title.trim(),
        description: draft.description.trim() || null,
        poll_type: editingId ? draft.poll_type : "custom",
        question: draft.question.trim(),
        options,
        group_ids: draft.group_ids,
        trigger_type: draft.trigger_type,
        hours_before: Number(draft.hours_before) || 2,
        hours_after: Number(draft.hours_after) || 1,
        scheduled_send_at:
          draft.trigger_type === "scheduled" && draft.scheduled_send_at
            ? new Date(draft.scheduled_send_at).toISOString()
            : null,
        closes_at: draft.closes_at
          ? new Date(draft.closes_at).toISOString()
          : null,
        publish:
          draft.trigger_type === "immediate" ||
          (draft.trigger_type === "scheduled" &&
            !isScheduledInFuture(draft.scheduled_send_at)),
      };
      if (editingId) {
        await clinicAPI.updateTrainingPoll(editingId, {
          ...payload,
          republish: false,
        });
        toast.success("Опрос обновлён");
      } else {
        const r = await clinicAPI.createTrainingPoll(payload);
        if (recurring) {
          toast.success(
            draft.trigger_type === "after_session"
              ? "Опрос включён — будет отправляться после каждой тренировки"
              : "Опрос включён — будет отправляться перед каждой тренировкой",
          );
        } else if (
          draft.trigger_type === "scheduled" &&
          isScheduledInFuture(draft.scheduled_send_at)
        ) {
          toast.success("Опрос запланирован");
        } else {
          toast.success(
            r.sent
              ? `Опрос создан, отправлено ${r.sent} клиентам`
              : "Опрос создан",
          );
        }
      }
      setEditorOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка сохранения");
    } finally {
      setSaving(false);
    }
  };

  const removePoll = async (poll) => {
    if (!window.confirm(`Удалить опрос «${poll.title}»?`)) return;
    try {
      await clinicAPI.deleteTrainingPoll(poll.id);
      toast.success("Удалено");
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    }
  };

  const pollCountLabel = `${polls.length} ${
    polls.length === 1 ? "опрос" : polls.length < 5 ? "опроса" : "опросов"
  }`;

  const pollsFootHint = (
    <p className={md.desktopPollsFootHint}>
      Опросы обратной связи не влияют на прогноз.{" "}
      <Link to="/manage/app/schedule">Календарь</Link> показывает ответы по
      занятию.
    </p>
  );

  const pollsMainContent = (
    <ActTarget stepId="polls" subId="polls_list" as="div">
    <>
      {loading ? (
        isApp ? (
          <Card>
            <p className={s.pollsEditorIntro}>Загрузка…</p>
          </Card>
        ) : (
          <p className={s.pollsEditorIntro}>Загрузка…</p>
        )
      ) : polls.length === 0 ? (
        isApp ? (
          <EmptyState
            title="Пока нет опросов"
            description="Создайте опрос явки — клиенты ответят в приложении"
            actionLabel="Создать опрос"
            onAction={openCreate}
          />
        ) : (
          <EmptyState
            title="Пока нет опросов"
            description="Создайте опрос явки — клиенты ответят в личном кабинете, а вы увидите прогноз до занятия."
          />
        )
      ) : isApp ? (
        <>
          {activePolls.length > 0 ? (
            <section>
              <h2 className={aa.hubSectionLabel}>Активные</h2>
              <HubListGroup>
                {activePolls.map((poll) => (
                  <SwipeableRow
                    key={poll.id}
                    actions={[
                      {
                        id: "results",
                        label: "Итоги",
                        tone: "neutral",
                        onClick: () => setResultsId(poll.id),
                      },
                      {
                        id: "edit",
                        label: "Изменить",
                        tone: "neutral",
                        onClick: () => openEdit(poll),
                      },
                      {
                        id: "delete",
                        label: "Удалить",
                        tone: "danger",
                        onClick: () => removePoll(poll),
                      },
                    ]}
                  >
                    <HubRow
                      title={poll.title}
                      meta={pollTypeLabel(poll.poll_type)}
                      sub={`${pollTriggerLabel(poll)} · ответили ${poll.stats?.responded ?? 0} из ${poll.stats?.sent ?? 0}`}
                      badge={
                        poll.stats?.forecast_coming != null
                          ? `${poll.stats.forecast_coming} приду`
                          : undefined
                      }
                      badgeTone="accent"
                      onClick={() => setResultsId(poll.id)}
                    />
                  </SwipeableRow>
                ))}
              </HubListGroup>
            </section>
          ) : null}
          {polls.filter((p) => p.status !== "active").length > 0 ? (
            <section>
              <h2 className={aa.hubSectionLabel}>Завершённые</h2>
              <HubListGroup>
                {polls
                  .filter((p) => p.status !== "active")
                  .map((poll) => (
                    <HubRow
                      key={poll.id}
                      title={poll.title}
                      meta={`${pollTypeLabel(poll.poll_type)} · ответили ${poll.stats?.responded ?? 0}`}
                      onClick={() => setResultsId(poll.id)}
                    />
                  ))}
              </HubListGroup>
            </section>
          ) : null}
        </>
      ) : (
        <>
          {activePolls.length > 0 ? (
            <section className={s.pollsActiveSection}>
              <h2 className={s.pollsSectionTitle}>Активные</h2>
              <ul className={isApp ? s.pollsCardList : md.desktopPollList}>
                {activePolls.map((poll) => (
                  <li key={poll.id}>
                    {isApp ? (
                    <Card className={s.pollsCard}>
                      <div className={s.pollsCardTop}>
                        <div>
                          <span className={s.pollsTypeBadge}>
                            {pollTypeLabel(poll.poll_type)}
                          </span>
                          <h3 className={s.pollsCardTitle}>{poll.title}</h3>
                        </div>
                        {poll.poll_type === "attendance" &&
                        poll.stats?.forecast_coming != null ? (
                          <div className={s.pollsCardForecast}>
                            <span className={s.pollsCardForecastVal}>
                              {poll.stats.forecast_coming}
                            </span>
                            <span className={s.pollsCardForecastLbl}>
                              приду
                            </span>
                          </div>
                        ) : null}
                      </div>
                      <p className={s.pollsCardMeta}>
                        {pollTriggerLabel(poll)}
                        {" · "}
                        Ответили {poll.stats?.responded ?? 0} из{" "}
                        {poll.stats?.sent ?? 0}
                        {poll.closes_at &&
                        inferTriggerType(poll) !== "before_session" &&
                        inferTriggerType(poll) !== "after_session"
                          ? ` · до ${formatDateRu(poll.closes_at)} ${formatTimeRu(poll.closes_at)}`
                          : ""}
                      </p>
                      <div className={s.pollsCardActions}>
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => setResultsId(poll.id)}
                        >
                          Результаты
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          onClick={() => openEdit(poll)}
                        >
                          Изменить
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          onClick={() => removePoll(poll)}
                        >
                          Удалить
                        </Button>
                      </div>
                    </Card>
                    ) : (
                      <div className={md.desktopPollCard}>
                        <div className={md.desktopPollCardTop}>
                          <div className={md.desktopPollRowMain}>
                            <span className={s.pollsTypeBadge}>
                              {pollTypeLabel(poll.poll_type)}
                            </span>
                            <h3 className={md.desktopPollRowTitle}>{poll.title}</h3>
                            <p className={md.desktopPollRowMeta}>
                              {pollTriggerLabel(poll)}
                              {" · "}
                              Ответили {poll.stats?.responded ?? 0} из{" "}
                              {poll.stats?.sent ?? 0}
                              {poll.closes_at &&
                              inferTriggerType(poll) !== "before_session" &&
                              inferTriggerType(poll) !== "after_session"
                                ? ` · до ${formatDateRu(poll.closes_at)} ${formatTimeRu(poll.closes_at)}`
                                : ""}
                            </p>
                          </div>
                          {poll.poll_type === "attendance" &&
                          poll.stats?.forecast_coming != null ? (
                            <div className={s.pollsCardForecast}>
                              <span className={s.pollsCardForecastVal}>
                                {poll.stats.forecast_coming}
                              </span>
                              <span className={s.pollsCardForecastLbl}>
                                приду
                              </span>
                            </div>
                          ) : null}
                        </div>
                        <div className={md.desktopPollCardActions}>
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => setResultsId(poll.id)}
                          >
                            Результаты
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            onClick={() => openEdit(poll)}
                          >
                            Изменить
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            onClick={() => removePoll(poll)}
                          >
                            Удалить
                          </Button>
                        </div>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {polls.filter((p) => p.status !== "active").length > 0 ? (
            <section>
              <h2 className={s.pollsSectionTitle}>Завершённые</h2>
              <ul className={isApp ? s.pollsCardList : md.desktopPollList}>
                {polls
                  .filter((p) => p.status !== "active")
                  .map((poll) => (
                    <li key={poll.id}>
                      {isApp ? (
                      <Card className={s.pollsCard}>
                        <h3 className={s.pollsCardTitle}>{poll.title}</h3>
                        <p className={s.pollsCardMeta}>
                          {pollTypeLabel(poll.poll_type)} · ответили{" "}
                          {poll.stats?.responded ?? 0}
                        </p>
                        <Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          onClick={() => setResultsId(poll.id)}
                        >
                          Смотреть
                        </Button>
                      </Card>
                      ) : (
                        <div className={md.desktopPollRow}>
                          <div className={md.desktopPollRowMain}>
                            <h3 className={md.desktopPollRowTitle}>{poll.title}</h3>
                            <p className={md.desktopPollRowMeta}>
                              {pollTypeLabel(poll.poll_type)} · ответили{" "}
                              {poll.stats?.responded ?? 0}
                            </p>
                          </div>
                          <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            onClick={() => setResultsId(poll.id)}
                          >
                            Смотреть
                          </Button>
                        </div>
                      )}
                    </li>
                  ))}
              </ul>
            </section>
          ) : null}
        </>
      )}
    </>
    </ActTarget>
  );

  const pollsOverlays = (
    <>
      {editorOpen ? (
        <PollEditor
          draft={draft}
          onChange={setDraft}
          groups={groups}
          sessions={sessions}
          onClose={() => setEditorOpen(false)}
          onSave={savePoll}
          saving={saving}
          isEdit={Boolean(editingId)}
          isApp={isApp}
        />
      ) : null}

      {resultsId ? (
        <PollResults pollId={resultsId} onClose={() => setResultsId(null)} isApp={isApp} />
      ) : null}
    </>
  );

  const pollsContent = (
    <>
      {pollsMainContent}
      {pollsOverlays}
    </>
  );

  const sectionTabs = POLL_MAILINGS_ENABLED
    ? [
        { id: "polls", label: "Опросы" },
        { id: "mailings", label: "Рассылки" },
      ]
    : [{ id: "polls", label: "Опросы" }];

  const handleAdd = () => {
    if (POLL_MAILINGS_ENABLED && section === "mailings") setMailingEditorOpen(true);
    else openCreate();
  };

  if (isApp) {
    return (
      <HubScreen
        title="Опросы"
        right={
          <ActTarget stepId="polls" subId="polls_create" as="span">
          <HubNavAdd onClick={handleAdd} ariaLabel="Создать опрос" />
          </ActTarget>
        }
      >
        <ActivationPageGuide stepIds={["polls"]} />
        {POLL_MAILINGS_ENABLED ? (
          <HubSegment tabs={sectionTabs} active={section} onChange={setSection} />
        ) : null}
        {section === "polls" || !POLL_MAILINGS_ENABLED ? (
          <>
            {polls.length > 0 ? (
              <span className={aa.hubCount}>{pollCountLabel}</span>
            ) : null}
            {pollsContent}
          </>
        ) : (
          <MailingsPanel
            groups={groups}
            editorOpen={mailingEditorOpen}
            onEditorOpenChange={setMailingEditorOpen}
          />
        )}
      </HubScreen>
    );
  }

  return (
    <div className={`${md.desktopHubPage} ${md.desktopPollsPage} ${s.pollsPage}`}>
      <ActivationPageGuide stepIds={["polls"]} />
      <header className={md.desktopHubPageHead}>
        <div className={md.desktopHubPageIntro}>
          <p className={md.desktopHubPageLead}>
            Опросы явки, отзывы и анкеты — вручную или по расписанию занятий.
          </p>
          {polls.length > 0 ? (
            <p className={md.desktopHubPageMeta}>{pollCountLabel}</p>
          ) : null}
        </div>
        <ActTarget stepId="polls" subId="polls_create" as="span">
        <Button type="button" onClick={handleAdd}>
          + Создать опрос
        </Button>
        </ActTarget>
      </header>

      {POLL_MAILINGS_ENABLED ? (
        <div className={md.desktopHubPanel}>
          <div className={md.desktopGroupTabs} role="tablist">
            {sectionTabs.map((t) => (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={section === t.id}
                className={
                  section === t.id ? md.desktopGroupTabActive : md.desktopGroupTab
                }
                onClick={() => setSection(t.id)}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className={md.desktopGroupTabBody}>
            {section === "polls" ? (
              <>
                <div className={md.desktopPollsPanelBody}>{pollsMainContent}</div>
                {pollsFootHint}
              </>
            ) : (
              <MailingsPanel
                groups={groups}
                editorOpen={mailingEditorOpen}
                onEditorOpenChange={setMailingEditorOpen}
              />
            )}
          </div>
        </div>
      ) : (
        <div className={`${md.desktopHubPanel} ${md.desktopPollsPanel}`}>
          <div className={md.desktopPollsPanelBody}>{pollsMainContent}</div>
          {pollsFootHint}
        </div>
      )}

      {pollsOverlays}
    </div>
  );
}
